import {
    makeStyles,
    Menu,
    MenuButton,
    MenuItemRadio,
    MenuList,
    MenuPopover,
    MenuTrigger,
    tokens,
} from '@fluentui/react-components';
import { Eye20Filled } from '@fluentui/react-icons';
import { useRoleContext, UserRoles } from 'hybridspace-common/lib/context';
import { PEOPLE } from 'hybridspace-home';
import { logUsage } from 'owa-analytics';
import loc from 'owa-localize';
import { observer } from 'owa-mobx-react';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { isPlacesExploreEnabled } from 'places-settings';
import React, { forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDefaultAdminScreenId } from '../../utils/useDefaultAdminScreenId';
import {
    hybridspaceAdmin as strings_hybridspaceAdmin,
    hybridspaceEmployee as strings_hybridspaceEmployee,
    hybridspaceViewAs as strings_hybridspaceViewAs,
} from './HybridspaceRoleSwitcher.locstring.json';

import type { MenuItemRadioProps } from '@fluentui/react-components';
import { shouldStartPlacesAnalyticsAlt } from 'hybridspace-performance-datapoints';
import {
    cancelPlacesAnalyticsPerfEventsAction,
    placesAnalyticsAppLoadAction,
} from 'places-fwk-actions';

const useStyles = makeStyles({
    eyeIcon: {
        marginRight: '0.3rem',
        '@media (forced-colors: active)': {
            color: 'ButtonText',
        },
    },
    menuPopOver: {
        marginTop: '0.07rem',
        width: '10.7rem',
    },
    viewAsText: {
        color: tokens.colorPaletteCornflowerForeground2,
        fontSize: '0.81rem',
        marginLeft: '0.52rem',
    },
    rolesList: {
        marginTop: '0.3rem',
    },
    userRole: {
        fontSize: '0.85rem',
        marginTop: '0.2rem',
        marginBottom: '0.2rem',
    },
    tooltipMainComponent: {
        display: 'flex',
        flexDirection: 'column',
    },
});

const defaultExport: React.MemoExoticComponent<
    React.ForwardRefExoticComponent<
        Omit<React.RefAttributes<HTMLButtonElement>, 'ref'> & React.RefAttributes<HTMLButtonElement>
    >
> = observer(
    forwardRef<HTMLButtonElement, {}>((_, ref) => {
        const classes = useStyles();
        const { userRole, setUserRole } = useRoleContext();

        const navigate = useNavigate();
        const maiboxInfo = getModuleContextMailboxInfo();
        const defaultEmployeeTabId = isPlacesExploreEnabled(maiboxInfo) ? `/${PEOPLE}` : `/`;
        const defaultAdminTabId = useDefaultAdminScreenId();

        const handleRoleSwitch = React.useCallback<
            Exclude<MenuItemRadioProps['onClick'], undefined>
        >(
            _event => {
                const currentRole = _event.currentTarget.id;
                const previousRole = userRole;

                if (currentRole === previousRole) {
                    return;
                }

                if (currentRole === UserRoles.Employee) {
                    setUserRole(UserRoles.Employee);

                    // This is to handle the case when user switches immediately to employee role after switching to admin role or directly after landing on space-analytics page.
                    // In this case, we should cancel all the perf markers for analytics page if they are already running.
                    cancelPlacesAnalyticsPerfEventsAction({
                        scenario: 'EmployeeRoleSwitchStart',
                        eventsToCancel: ['All'],
                    });
                    navigate(defaultEmployeeTabId);
                } else {
                    setUserRole(UserRoles.Admin);
                    // start the perf mark for admin role switch
                    if (shouldStartPlacesAnalyticsAlt('/places' + defaultAdminTabId)) {
                        placesAnalyticsAppLoadAction({
                            scenario: 'AdminRoleSwitchStart',
                        });
                    }
                    navigate(defaultAdminTabId);
                }

                logUsage('HybridSpaceRoleSwitcher', maiboxInfo, {
                    toRole: currentRole,
                    fromRole: previousRole,
                });
            },
            [userRole, setUserRole, navigate, defaultEmployeeTabId, defaultAdminTabId, maiboxInfo]
        );

        const checkedValues = React.useMemo(() => {
            return {
                roles: [userRole],
            };
        }, [userRole]);

        return (
            <Menu checkedValues={checkedValues}>
                <MenuTrigger>
                    <MenuButton appearance="subtle" ref={ref}>
                        <Eye20Filled className={classes.eyeIcon} />
                        {userRole === UserRoles.Admin
                            ? loc(strings_hybridspaceAdmin)
                            : loc(strings_hybridspaceEmployee)}
                    </MenuButton>
                </MenuTrigger>

                <MenuPopover className={classes.menuPopOver}>
                    <span className={classes.viewAsText}>{loc(strings_hybridspaceViewAs)}</span>
                    <MenuList className={classes.rolesList}>
                        <MenuItemRadio
                            name="roles"
                            value={UserRoles.Admin}
                            id={UserRoles.Admin}
                            className={classes.userRole}
                            onClick={handleRoleSwitch}
                        >
                            {loc(strings_hybridspaceAdmin)}
                        </MenuItemRadio>
                        <MenuItemRadio
                            name="roles"
                            value={UserRoles.Employee}
                            id={UserRoles.Employee}
                            className={classes.userRole}
                            onClick={handleRoleSwitch}
                        >
                            {loc(strings_hybridspaceEmployee)}
                        </MenuItemRadio>
                    </MenuList>
                </MenuPopover>
            </Menu>
        );
    }),
    'HybridspaceRoleSwitcher'
);
export default defaultExport;
